import { Controller } from "@hotwired/stimulus";

/**
 * Stacking Cards Controller
 *
 * Creates a scroll-driven stacking card effect where cards stick to the viewport
 * and stack on top of each other as the user scrolls.
 *
 * The effect:
 * 1. Cards start with vertical spacing between them
 * 2. As user scrolls, each card scrolls up and "sticks" at its position
 * 3. The next card scrolls over the previous one
 * 4. Cards that are covered scale down slightly and darken
 *
 * Usage:
 * - Wrap cards in a container with data-controller="stacking-cards"
 * - Each card should have data-stacking-cards-target="card"
 * - Set wrapper height to accommodate all cards (e.g., 300vh for 3 cards)
 */
export default class extends Controller<HTMLElement> {
  static targets = ["card", "wrapper"];

  declare readonly cardTargets: HTMLElement[];
  declare readonly wrapperTarget: HTMLElement;

  private scrollHandler: (() => void) | null = null;
  private resizeHandler: (() => void) | null = null;
  private cardResizeObserver: ResizeObserver | null = null;
  private ticking: boolean = false;

  // The scroll distance allocated per card transition
  private scrollPerCard: number = 0;

  // Breakpoint for disabling stacking effect (matches Tailwind's lg: breakpoint)
  private readonly mobileBreakpoint: number = 1024;

  connect(): void {
    this.calculateDimensions();
    this.setupScrollHandler();
    this.setupResizeHandler();
    this.setupCardResizeObserver();
    this.updateCards();
  }

  disconnect(): void {
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
    if (this.cardResizeObserver) {
      this.cardResizeObserver.disconnect();
    }
  }

  private calculateDimensions(): void {
    const numCards = this.cardTargets.length;
    if (numCards === 0) return;

    if (this.isMobile()) {
      this.wrapperTarget.style.height = "";
      this.scrollPerCard = 0;
      return;
    }

    // Each card's scroll allocation (scrollPerCard = wrapperHeight / numCards)
    // must be large enough for the last card to remain stuck and readable.
    // stickingDuration = scrollPerCard - lastCardBottom
    // We need: stickingDuration >= minStickingScroll
    // Therefore: wrapperHeight >= numCards * (lastCardBottom + minStickingScroll)
    const lastCard = this.cardTargets[numCards - 1];
    const lastCardStickyTop =
      parseFloat(getComputedStyle(lastCard).top) || 0;
    const lastCardBottom = lastCardStickyTop + lastCard.offsetHeight;
    const minStickingScroll = 0;
    const minHeight = numCards * (lastCardBottom + minStickingScroll);

    // Use the CSS-defined height (vh-based) unless it's too short
    this.wrapperTarget.style.height = "";
    const cssHeight = this.wrapperTarget.offsetHeight;

    const wrapperHeight = Math.max(cssHeight, minHeight);
    if (wrapperHeight > cssHeight) {
      this.wrapperTarget.style.height = `${wrapperHeight}px`;
    }

    this.scrollPerCard = wrapperHeight / numCards;
  }

  private setupScrollHandler(): void {
    this.scrollHandler = () => {
      if (!this.ticking) {
        requestAnimationFrame(() => {
          this.updateCards();
          this.ticking = false;
        });
        this.ticking = true;
      }
    };

    window.addEventListener("scroll", this.scrollHandler, { passive: true });
  }

  private setupResizeHandler(): void {
    this.resizeHandler = () => {
      this.calculateDimensions();
      this.updateCards();
    };

    window.addEventListener("resize", this.resizeHandler, { passive: true });
  }

  // Watches card elements for size changes (e.g., from web font loading) and
  // recalculates wrapper height so the stacking effect has enough scroll room.
  private setupCardResizeObserver(): void {
    this.cardResizeObserver = new ResizeObserver(() => {
      this.calculateDimensions();
      this.updateCards();
    });

    this.cardTargets.forEach((card) => {
      this.cardResizeObserver!.observe(card);
    });
  }

  private isMobile(): boolean {
    return window.innerWidth < this.mobileBreakpoint;
  }

  private updateCards(): void {
    const wrapperRect = this.wrapperTarget.getBoundingClientRect();
    const scrollIntoWrapper = -wrapperRect.top;
    const numCards = this.cardTargets.length;

    // On mobile, disable all stacking effects - cards just stack like normal content
    if (this.isMobile()) {
      this.cardTargets.forEach((card) => {
        card.style.transform = "";
        card.style.filter = "";
        card.style.opacity = "";
        card.style.zIndex = "";
      });
      return;
    }

    this.cardTargets.forEach((card, index) => {
      const isLastCard = index === numCards - 1;
      // Calculate this card's scroll progress
      // Each card "activates" at a different scroll position
      const cardActivationPoint = index * this.scrollPerCard;
      const cardProgress =
        (scrollIntoWrapper - cardActivationPoint) / this.scrollPerCard;

      // Z-index strategy: all cards stay below header (z-50)
      // Approaching cards need higher z-index than stacked cards to scroll over them
      // Range: 20-40 (all below header's z-50)
      if (cardProgress < 0) {
        // Card hasn't reached its activation point yet - it's below/approaching
        card.style.transform = "scale(1)";
        card.style.filter = "brightness(1)";
        card.style.opacity = "1";
        // Approaching cards: z-index 30+ so they scroll over stacked cards (20+)
        card.style.zIndex = String(30 + index);
      } else if (cardProgress <= 1) {
        // Card is active/being covered by the next card
        // Last card never gets covered, so it stays at full brightness/scale
        if (isLastCard) {
          card.style.transform = "scale(1)";
          card.style.filter = "brightness(1)";
        } else {
          const scale = 1 - cardProgress * 0.05; // Scale from 1 to 0.95
          const brightness = 1 - cardProgress * 0.3; // Dim from 1 to 0.7
          card.style.transform = `scale(${scale})`;
          card.style.filter = `brightness(${brightness})`;
        }
        card.style.opacity = "1";
        card.style.zIndex = String(20 + index);
      } else {
        // Card has been fully covered (last card shouldn't reach this state)
        card.style.transform = "scale(0.95)";
        card.style.filter = "brightness(0.7)";
        card.style.opacity = "1";
        card.style.zIndex = String(20 + index);
      }
    });
  }
}
